import { AlertTriangle, CheckCircle2 } from "lucide-react";
import type { DeleteExpiredVmsResult } from "@/types/admin";

/**
 * The report of one delete request: the rows that went through under a success heading, with what
 * happens next, and the refused rows with the backend's own reason.
 */
export function ExpiredVmsResult({
  result,
  successHeading,
  successNote,
}: {
  result: DeleteExpiredVmsResult;
  /** The heading over the rows that went through, e.g. "Deletion started for 2 VMs". */
  successHeading: string;
  /** What happens to those rows next, when it is not obvious from the heading. */
  successNote?: string;
}) {
  const failed = result.results.filter((row) => !row.success);
  const succeeded = result.results.filter((row) => row.success);

  return (
    <div className="space-y-4">
      {succeeded.length > 0 && (
        <div className="overflow-hidden rounded-md border border-ok/30 bg-ok-soft">
          <div className="space-y-0.5 border-b border-ok/30 px-3 py-2">
            <p className="flex items-center gap-2 text-sm font-medium text-ok-ink">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
              {successHeading}
            </p>
            {successNote && (
              <p className="pl-6 text-xs text-muted-foreground">
                {successNote}
              </p>
            )}
          </div>
          <ul className="max-h-40 divide-y divide-ok/20 overflow-y-auto bg-background text-sm">
            {succeeded.map((row) => (
              <li key={row.resourceId} className="truncate px-3 py-1.5">
                {row.resourceName || row.resourceId}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The failed rows, each with the backend's own reason: the operator has to know which ones were
          left untouched and why, so this is drawn as the error block of the report, not a plain table. */}
      {failed.length > 0 && (
        <div
          role="alert"
          className="max-h-64 overflow-y-auto rounded-md border border-destructive/45 bg-destructive-soft"
        >
          <p className="flex items-center gap-2 border-b border-destructive/45 px-3 py-2 text-sm font-medium text-destructive-ink">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            {failed.length === 1
              ? "1 failed and is unchanged"
              : `${failed.length} failed and are unchanged`}
          </p>
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="p-2 font-medium">Name</th>
                <th className="p-2 font-medium">Code</th>
                <th className="p-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {failed.map((row) => (
                <tr key={row.resourceId} className="border-t align-top">
                  <td className="p-2 font-medium">
                    {row.resourceName || row.resourceId}
                  </td>
                  <td className="p-2 font-mono">{row.errorCode}</td>
                  {/* The infrastructure's own sentence, not a rewrite. */}
                  <td className="p-2 text-destructive-ink">
                    {row.errorMessage}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
